/** File with a path relative to the dropped or picked folder root(s). */
export type FolderUploadFile = File & { webkitRelativePath: string };

export function canUseDirectoryPicker(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

export function hasFolderStructure(files: Iterable<File>): boolean {
  for (const file of files) {
    const rel =
      (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? '';
    if (rel.includes('/')) return true;
  }
  return false;
}

function withRelativePath(file: File, relativePath: string): FolderUploadFile {
  const tagged = file as FolderUploadFile;
  try {
    Object.defineProperty(tagged, 'webkitRelativePath', {
      value: relativePath,
      configurable: true,
      enumerable: true
    });
  } catch {
    // Some environments may not allow redefining; path is still on the object for readers.
    (tagged as { webkitRelativePath: string }).webkitRelativePath =
      relativePath;
  }
  return tagged;
}

export async function collectFilesFromDirectoryHandle(
  handle: FileSystemDirectoryHandle,
  basePath = ''
): Promise<FolderUploadFile[]> {
  const out: FolderUploadFile[] = [];
  for await (const [name, entry] of handle.entries()) {
    const rel = basePath ? `${basePath}/${name}` : name;
    if (entry.kind === 'file') {
      const file = await entry.getFile();
      if (file.size > 0) out.push(withRelativePath(file, rel));
    } else if (entry.kind === 'directory') {
      out.push(...(await collectFilesFromDirectoryHandle(entry, rel)));
    }
  }
  return out;
}

export async function pickDirectoryWithFsApi(): Promise<
  FolderUploadFile[] | null
> {
  if (!canUseDirectoryPicker()) return null;
  const handle = await window.showDirectoryPicker({ mode: 'read' });
  return collectFilesFromDirectoryHandle(handle, handle.name);
}

async function readDirectoryEntry(
  entry: FileSystemDirectoryEntry,
  basePath: string
): Promise<FolderUploadFile[]> {
  const reader = entry.createReader();
  const out: FolderUploadFile[] = [];

  const readBatch = (): Promise<FileSystemEntry[]> =>
    new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });

  for (;;) {
    const batch = await readBatch();
    if (batch.length === 0) break;
    for (const child of batch) {
      const rel = basePath ? `${basePath}/${child.name}` : child.name;
      if (child.isFile) {
        const file = await new Promise<File>((resolve, reject) => {
          (child as FileSystemFileEntry).file(resolve, reject);
        });
        if (file.size > 0) out.push(withRelativePath(file, rel));
      } else if (child.isDirectory) {
        out.push(
          ...(await readDirectoryEntry(child as FileSystemDirectoryEntry, rel))
        );
      }
    }
  }
  return out;
}

/** Collect files from drag-and-drop, preserving folder structure for one or more roots. */
export async function collectFilesFromDataTransfer(
  dataTransfer: DataTransfer
): Promise<FolderUploadFile[]> {
  const items = dataTransfer.items;
  if (!items?.length) {
    return tagFlatFileList(dataTransfer.files);
  }

  const roots: FileSystemEntry[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item?.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry?.();
    if (entry) roots.push(entry);
  }

  if (roots.length === 0) {
    return tagFlatFileList(dataTransfer.files);
  }

  const out: FolderUploadFile[] = [];
  for (const entry of roots) {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) => {
        (entry as FileSystemFileEntry).file(resolve, reject);
      });
      if (file.size > 0) out.push(withRelativePath(file, entry.name));
    } else if (entry.isDirectory) {
      out.push(
        ...(await readDirectoryEntry(
          entry as FileSystemDirectoryEntry,
          entry.name
        ))
      );
    }
  }
  return out;
}

function tagFlatFileList(fileList: FileList | File[]): FolderUploadFile[] {
  return Array.from(fileList)
    .filter((f) => f.size > 0)
    .map((f) => {
      const rel =
        (f as File & { webkitRelativePath?: string }).webkitRelativePath ??
        f.name;
      return withRelativePath(f, rel);
    });
}
